/* ──────────────────────────────────────────────────────────────
   Heat-map horario  (porcentaje profesores ocupados)
   – eje-Y  →  L-D
   – eje-X  →  08 … 20 h (14 columnas)
   – color  →  0 % (rojo) → 100 % (verde)
   ────────────────────────────────────────────────────────────── */
import React, { useMemo, useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  Stack,
  Typography,
  LinearProgress,
  Alert,
  Paper,
  Tooltip,
  Box,
  Autocomplete,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useHeatmapHorarioReport } from "../../../hooks/useReportJob";
import { useCentros } from "../../../hooks/useCentros";
import { useTipos } from "../../../hooks/useTiposAsignatura";
import type { HeatCell } from "../../../api/reports"; // { dow, hora, pct }
import { downloadReportFile } from "../../../components/utils/downloadXlsx";

/* ejes */
const HORAS = Array.from({ length: 14 }, (_, i) => 8 + i); // 08-21-1
const DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"]; // dow 0-6

/* 0 % rojo  → 100 % verde */
const pctToColor = (p: number) => {
  const clamped = Math.max(0, Math.min(100, p));
  const hue = (clamped * 120) / 100; // 0 → red, 120 → green
  return `hsl(${hue}, 70%, 45%)`;
};

const keyFor = (dow: number, hora: number) => `${dow}-${hora}`;

/* helper para intersecar sets sin romper la inferencia de TS */
const intersectSets = (a: Set<number>, b: Set<number>) => {
  const res = new Set<number>();
  a.forEach((v) => {
    if (b.has(v)) res.add(v);
  });
  return res;
};

export default function HeatmapHorarioPage() {
  const { t } = useTranslation<"translation">();
  /* worker */
  const { run, useStatus } = useHeatmapHorarioReport();
  const [jobId, setJobId] = useState<string>();
  const { data: status } = useStatus(jobId);

  /* filtros */
  const [centroSel, setCentSel] = useState<{
    id: number;
    nombre: string;
  } | null>(null);
  const [tipoSel, setTipoSel] = useState<{ id: number; nombre: string } | null>(null);

  const { rows: centros } = useCentros();
  const { rows: tipos } = useTipos();

  /* selección de celdas + modo de coincidencia */
  const [selected, setSelected] = useState<string[]>([]);
  const [matchMode, setMatchMode] = useState<"all" | "any">("all"); // all = intersección, any = unión

  /* lanzar informe */
  const lanzar = async () => {
    const { jobId } = await run({
      desde: "2000-01-01",
      hasta: dayjs().format("YYYY-MM-DD"),
      centroId: centroSel?.id,
      tipoId: tipoSel?.id,
    });
    setSelected([]); // limpiar selección al regenerar
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  /* matriz día × hora (para colorear) */
  const matrix: number[][] = DIAS.map(() => HORAS.map(() => 0));
  (status?.result?.rows ?? []).forEach((r: HeatCell) => {
    const fila = matrix[r.dow];
    const col = HORAS.indexOf(r.hora);
    if (fila && col !== -1) fila[col] = r.pct; // 0-100
  });

  /* índice celda → [profesorId] devuelto por el worker (aceptamos varias claves posibles) */
  const cellIndexRaw: Record<string, number[]> | null =
    (status?.result as any)?.index ??
    (status?.result as any)?.cellIndex ??
    (status?.result as any)?.slotsIndex ??
    (status?.result as any)?.indexByCell ??
    (status?.result as any)?.byCell ??
    (status?.result as any)?.celdaIndex ??
    (status?.result as any)?.indice ??
    null;

  const cellIndex = cellIndexRaw ?? null;

  /* precomputo de claves del índice para búsqueda rápida */
  const indexKeySet = useMemo(() => new Set(Object.keys(cellIndex ?? {})), [cellIndex]);

  /* lista de profesores del worker */
  const allProfes = (status?.result?.profesores ?? []) as any[];

  /* detectar si tenemos campos de centro/tipo en los objetos para poder filtrar en el front */
  const haveCentroField = useMemo(
    () => allProfes.some((p: any) => p?.centroId != null || Array.isArray(p?.centroIds)),
    [allProfes]
  );
  const haveTipoField = useMemo(
    () => allProfes.some((p: any) => p?.tipoId != null || Array.isArray(p?.tipoIds)),
    [allProfes]
  );

  /* aplicar filtros de centro/tipo en el front SOLO si existen esos campos;
     si no, asumimos que el backend ya filtró. */
  const baseProfes = useMemo(() => {
    let list: any[] = allProfes;
    if (centroSel && haveCentroField) {
      list = list.filter(
        (p: any) => p.centroId === centroSel.id || p.centroIds?.includes?.(centroSel.id)
      );
    }
    if (tipoSel && haveTipoField) {
      list = list.filter((p: any) => p.tipoId === tipoSel.id || p.tipoIds?.includes?.(tipoSel.id));
    }
    return list;
  }, [allProfes, centroSel, tipoSel, haveCentroField, haveTipoField]);

  /* helpers para resolver ids a partir de una clave seleccionada "rowIdx-hora" */
  const parseSelectedKey = (k: string): { rowIdx: number; hora: number } => {
    const [a, b] = k.split("-");
    const rowIdx = Number(a);
    const hora = Number(b);
    return { rowIdx, hora };
  };
  const pad2 = (n: number) => String(n).padStart(2, "0");

  // Dado un (rowIdx, hora) del UI, intenta varias formas de clave para encontrar la que usa el índice
  const resolveIndexKey = (rowIdx: number, hora: number): string | null => {
    if (!cellIndex) return null;
    const dowCandidates: string[] = [
      String(rowIdx), // 0..6 Lun..Dom (UI)
      String((rowIdx + 1) % 7), // 0..6 Dom..Sáb (JS domingo=0)
      String((rowIdx + 6) % 7), // desplazamiento inverso
      String(rowIdx + 1), // 1..7 Lun..Dom
      String(rowIdx === 6 ? 7 : rowIdx + 1), // 1..7 con Domingo=7
    ];
    const horaCandidates: string[] = [
      String(hora), // "8"
      pad2(hora), // "08"
      `${pad2(hora)}:00`, // "08:00"
    ];
    const seps = ["-", "|", ":", "_", ","];

    for (const d of dowCandidates) {
      for (const h of horaCandidates) {
        for (const s of seps) {
          const k1 = `${d}${s}${h}`; // dow-sep-hora
          if (indexKeySet.has(k1)) return k1;
          const k2 = `${h}${s}${d}`; // hora-sep-dow
          if (indexKeySet.has(k2)) return k2;
        }
      }
    }
    return null;
  };

  const getIdsForSelectedKey = (k: string): number[] => {
    if (!cellIndex) return [];
    const { rowIdx, hora } = parseSelectedKey(k);
    const realKey = resolveIndexKey(rowIdx, hora);
    if (!realKey) return [];
    const ids = cellIndex[realKey];
    return Array.isArray(ids) ? ids : [];
  };

  /* lista de profesores filtrada por la selección */
  const filteredProfes = useMemo(() => {
    // Sin selección → devolvemos la base filtrada por centro/tipo (o la del backend)
    if (selected.length === 0) return baseProfes;

    // Si no hay índice, degradamos mostrando la base (mejor que 0 por un fallo de clave)
    if (!cellIndex) return baseProfes;

    if (matchMode === "all") {
      // intersección: deben tener dispo en TODAS las celdas seleccionadas
      let acc: Set<number> | null = null;
      for (const k of selected) {
        const ids = new Set<number>(getIdsForSelectedKey(k));
        acc = acc ? intersectSets(acc, ids) : ids;
        if (acc.size === 0) break;
      }
      return baseProfes.filter((p) => acc?.has(p.id) ?? false);
    } else {
      // unión: dispo en ALGUNA celda seleccionada
      const acc = new Set<number>();
      selected.forEach((k) => getIdsForSelectedKey(k).forEach((id) => acc.add(id)));
      return baseProfes.filter((p) => acc.has(p.id));
    }
  }, [cellIndex, selected, baseProfes, matchMode]);

  /* limpiar selección al cambiar filtros */
  useEffect(() => {
    setSelected([]);
  }, [centroSel?.id, tipoSel?.id]);

  /* ancho mínimo de la malla: etiqueta + 14 celdas de 32 px */
  const minWidth = 80 + HORAS.length * 32;

  const toggleCell = (dow: number, hora: number) => {
    const k = keyFor(dow, hora);
    setSelected((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t("heatmapHorario.titulo")}</Typography>

      {/* filtros */}
      <Stack direction="row" spacing={2} flexWrap="wrap">
        <Autocomplete
          options={centros.filter((c) => c.nombre !== "OTHER")}
          getOptionLabel={(o) => o.nombre}
          value={centroSel}
          onChange={(_, v) => setCentSel(v)}
          renderInput={(p) => (
            <TextField {...p} label={t("heatmapHorario.filtros.centro")} size="small" />
          )}
          sx={{ width: 220 }}
        />
        <Autocomplete
          options={tipos}
          getOptionLabel={(o) => o.nombre}
          value={tipoSel}
          onChange={(_, v) => setTipoSel(v)}
          renderInput={(p) => (
            <TextField {...p} label={t("heatmapHorario.filtros.tipo")} size="small" />
          )}
          sx={{ width: 220 }}
        />

        <Button variant="contained" onClick={lanzar}>
          {t("heatmapHorario.filtros.generar")}
        </Button>
      </Stack>

      {/* progreso */}
      {status && !status.done && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="body2">
            {t("heatmapHorario.estado.generando", {
              progress: status.progress,
            })}
          </Typography>
        </Stack>
      )}

      {/* resultado */}
      {status?.done && status.result && (
        <>
          <Alert severity="success" sx={{ width: "fit-content" }}>
            {t("heatmapHorario.estado.exito")}{" "}
            {!!status.result.url && (
              <Button
                variant="text"
                size="small"
                onClick={handleDownload}
                sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
              >
                {t("heatmapHorario.estado.descargar")}
              </Button>
            )}
          </Alert>

          <Paper sx={{ p: 2, overflowX: "auto" }}>
            <Box sx={{ minWidth }}>
              {/* cabecera horas */}
              <Stack direction="row" spacing={0.5} sx={{ pl: 10, mb: 1 }}>
                {HORAS.map((h) => (
                  <Box key={`h-${h}`} sx={{ width: 32, textAlign: "center", fontSize: 12 }}>
                    {String(h).padStart(2, "0")}h
                  </Box>
                ))}
              </Stack>

              {/* grid 7 × 14 */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `80px repeat(${HORAS.length}, 32px)`,
                  gridAutoRows: "32px",
                  gap: 0.5,
                }}
              >
                {DIAS.map((dia, rowIdx) => (
                  <React.Fragment key={dia}>
                    {/* etiqueta día */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        pr: 1,
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      {dia}
                    </Box>

                    {HORAS.map((h, colIdx) => {
                      const pct = matrix[rowIdx][colIdx];
                      const k = keyFor(rowIdx, h);
                      const isSel = selected.includes(k);
                      return (
                        <Tooltip
                          key={`${dia}-${h}`}
                          title={t("heatmapHorario.tooltip.celda", {
                            dia,
                            hora: String(h).padStart(2, "0"),
                            pct: pct.toFixed(0),
                          })}
                          arrow
                          placement="top"
                        >
                          <Box
                            role="button"
                            aria-pressed={isSel}
                            tabIndex={0}
                            onClick={() => toggleCell(rowIdx, h)}
                            onKeyDown={(e) =>
                              (e.key === "Enter" || e.key === " ") && toggleCell(rowIdx, h)
                            }
                            sx={{
                              width: 32,
                              height: 32,
                              cursor: "pointer",
                              bgcolor: pctToColor(pct),
                              borderRadius: 0.5,
                              outline: isSel ? "2px solid #1976d2" : "1px solid rgba(0,0,0,0.05)",
                              transition: "outline 150ms",
                              "&:hover": { outline: "2px solid #1976d2" },
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </Paper>

          {/* Controles de selección */}
          <Paper
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Chip label={`${selected.length} slots seleccionados`} />
            <ToggleButtonGroup
              size="small"
              value={matchMode}
              exclusive
              onChange={(_, v) => v && setMatchMode(v)}
            >
              <ToggleButton value="all">Coinciden en TODOS</ToggleButton>
              <ToggleButton value="any">Coinciden en ALGUNO</ToggleButton>
            </ToggleButtonGroup>
            <Button size="small" onClick={() => setSelected([])} disabled={selected.length === 0}>
              Limpiar selección
            </Button>
            <Box sx={{ ml: "auto", fontSize: 14 }}>
              {filteredProfes.length} profesor(es) encontrados
            </Box>
          </Paper>

          {/* Listado según selección */}
          {filteredProfes.length > 0 ? (
            <Paper sx={{ p: 2, maxWidth: 380 }}>
              <Typography variant="h6" gutterBottom>
                {t("heatmapHorario.listaProfes.titulo", {
                  count: filteredProfes.length,
                })}
              </Typography>

              <Stack spacing={0.5}>
                {filteredProfes.map((p) => (
                  <Box key={p.id} sx={{ fontSize: 14 }}>
                    • {p.nombre}
                  </Box>
                ))}
              </Stack>
            </Paper>
          ) : (
            <Alert severity="info" sx={{ width: "fit-content" }}>
              {selected.length > 0
                ? "No hay profesores con disponibilidad para la selección actual."
                : "No hay profesores disponibles."}
            </Alert>
          )}
        </>
      )}
    </Stack>
  );
}
